import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, Search } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("dts_admin_pw") || "");
  const [input, setInput] = useState("");
  const [items, setItems] = useState([]);
  const [callStats, setCallStats] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchQuotes = useCallback(
    async (pw, q) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API}/admin/quotes`, {
          params: { search: q || "" },
          headers: { "X-Admin-Password": pw },
        });
        setItems(res.data.items);
        try {
          const st = await axios.get(`${API}/admin/call-clicks`, { headers: { "X-Admin-Password": pw } });
          setCallStats(st.data);
        } catch {
          /* stats are non-blocking */
        }
        sessionStorage.setItem("dts_admin_pw", pw);
        if (!password) setPassword(pw);
        return true;
      } catch (e) {
        if (e.response?.status === 401) {
          setError("Wrong password.");
          sessionStorage.removeItem("dts_admin_pw");
          setPassword("");
        } else {
          setError("Could not load submissions. Try again.");
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [password]
  );

  useEffect(() => {
    if (password) fetchQuotes(password, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!password) return undefined;
    const t = setTimeout(() => fetchQuotes(password, search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/quotes/export`, {
        headers: { "X-Admin-Password": password },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "quote-requests.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed. Try again.");
    }
  };

  if (!password) {
    return (
      <div className="admin-page">
        <div className="wrap" style={{ maxWidth: 460 }}>
          <div className="eyebrow"><span>Admin</span></div>
          <h1 style={{ fontSize: 30, fontWeight: 800 }}>Quote submissions</h1>
          <form
            className="quote-card"
            style={{ marginTop: 28 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) fetchQuotes(input.trim(), "");
            }}
          >
            <div className="field">
              <label htmlFor="admin-pw">Admin password</label>
              <input
                id="admin-pw"
                data-testid="admin-password-input"
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoFocus
              />
              {error && <span className="field-err" data-testid="admin-login-error">{error}</span>}
            </div>
            <button className="btn btn-primary" style={{ marginTop: 20 }} type="submit" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Checking…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="wrap">
        <div className="eyebrow"><span>Admin — Quote submissions</span></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
          <h1 style={{ fontSize: 30, fontWeight: 800 }}>
            {items.length} submission{items.length === 1 ? "" : "s"}
          </h1>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={exportCsv} data-testid="admin-export-csv-btn">
              <Download size={16} aria-hidden="true" /> Export CSV
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                sessionStorage.removeItem("dts_admin_pw");
                setPassword("");
                setItems([]);
              }}
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>

        {callStats && (
          <div className="calc-results" style={{ marginTop: 28 }} data-testid="admin-call-stats">
            <div className="calc-tile">
              <div className="v tnum" data-testid="admin-call-total">{callStats.total.toLocaleString("en-US")}</div>
              <div className="l">Tap-to-call — total</div>
            </div>
            {Object.entries(callStats.by_source)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([src, n]) => (
                <div className="calc-tile" key={src}>
                  <div className="v tnum">{n.toLocaleString("en-US")}</div>
                  <div className="l">Calls via {src}</div>
                </div>
              ))}
          </div>
        )}

        {callStats?.by_day && (
          <div className="calc-card" style={{ marginTop: 20, padding: "24px 24px 12px" }} data-testid="admin-call-chart">
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              <span>Tap-to-call — last 14 days</span>
            </div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={callStats.by_day} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="#C6D1DE" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fill: "#4C5A6B" }}
                    axisLine={{ stroke: "#C6D1DE" }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fill: "#4C5A6B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(15,86,168,0.06)" }}
                    contentStyle={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, border: "1px solid #C6D1DE", borderRadius: 3 }}
                    formatter={(v) => [v, "taps"]}
                  />
                  <Bar dataKey="count" fill="#0F56A8" radius={[2, 2, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="field" style={{ marginTop: 24, maxWidth: 420, position: "relative" }}>
          <label htmlFor="admin-search">Search</label>
          <input
            id="admin-search"
            data-testid="admin-search-input"
            type="search"
            placeholder="Name, phone, email, city, details…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && <p className="field-err" data-testid="admin-error">{error}</p>}

        <div className="admin-table-scroll">
          <table className="admin-table" data-testid="admin-quotes-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>City / State</th>
                <th>Building</th>
                <th>Details</th>
                <th>Heard via</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ color: "var(--muted)", padding: 28 }} data-testid="admin-empty-state">
                    {loading ? "Loading…" : "No submissions yet."}
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it.id} data-testid={`admin-row-${it.id}`}>
                    <td className="mono tnum" style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>
                      {new Date(it.created_at).toLocaleString()}
                    </td>
                    <td style={{ fontWeight: 600 }}>{it.name}</td>
                    <td className="mono tnum" style={{ fontSize: 13 }}>{it.phone}</td>
                    <td>{it.email}</td>
                    <td>{it.city_state || "—"}</td>
                    <td>{it.building_type || "—"}</td>
                    <td style={{ maxWidth: 340, whiteSpace: "pre-wrap" }}>{it.project_details || "—"}</td>
                    <td>{it.heard_about || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
